


import { Component, Vue } from "vue-property-decorator";

import { AgentKb } from "App";
import { EventBus } from "App/IOC";
import { Column, PagerSorter } from "App/Models";
import { AgentKbConversation, AgentKbFilters } from "App/Models/AgentKb";

import Table from "Web/Components/Table/Index.vue";

import Thread from "./Thread.vue";
import { CompanyOf, LoadCompanies } from "./Companies";

/* REPONSES IA, LES CONVERSATIONS ( Fred, 26/09/2026 ) — une ligne par fil :
   premiere question, nombre de tours, pouces, transferts, cloture
   ( `/console/conversations` ). Un clic ouvre le fil entier a droite.

   C'est ici qu'on voit ce qu'un echange isole ne montre pas : un fil de six
   tours ou l'agent tourne en rond, un fil jamais clos, un fil transmis.

   ⚠️ Piege du service : les filtres portent sur les ECHANGES, puis on regroupe.
   « Pouce en bas » rend les fils qui contiennent AU MOINS un pouce en bas, et
   `tours` compte alors les echanges retenus par le filtre, pas tout le fil —
   d'ou le nombre de tours affiche seulement sans filtre de vote.
   ⚠️ `to` est EXCLUSIF : on envoie le lendemain du dernier jour. */

type Period = 7 | 30 | 90 | 365;

type Kind = "all" | "down" | "sent";

interface ConversationRow extends AgentKbConversation {
    Id: string;
    Start: string;
    User: string;
    Question: string;
    Turns: string;
    Votes: string;
    Transfers: string;
    Closed: string;
}

function isoDay( date: Date ): string {
    const month = String( date.getMonth() + 1 ).padStart( 2, "0" );
    const day = String( date.getDate() ).padStart( 2, "0" );

    return `${ date.getFullYear() }-${ month }-${ day }`;
}

@Component( { components: { Table, Thread } } )
export default class AgentKbConversationsView extends Vue {
    private readonly periods: Period[] = [ 7, 30, 90, 365 ];
    private readonly kinds: Kind[] = [ "all", "down", "sent" ];

    private period: Period = 30;
    private kind: Kind = "all";
    private userEmail: string = "";
    private timer: number = 0;

    private rows: ConversationRow[] = [];
    private loading: boolean = false;
    private columns: Column[] = [];
    private pagerSorter: PagerSorter = new PagerSorter( 25 );

    private selected: ConversationRow | null = null;

    private alive: boolean = true;

    private created(): void {
        this.columns = [
            Column.Static( "Start", this.$t( "AGENTKB_columnDate" ) as string ),
            Column.Static( "User", this.$t( "AGENTKB_columnUser" ) as string ),
            Column.Static( "Question", this.$t( "AGENTKB_conversationsFirst" ) as string ),
            Column.Static( "Turns", this.$t( "AGENTKB_conversationsTurns" ) as string ),
            Column.Static( "Votes", this.$t( "AGENTKB_usersVotes" ) as string ),
            Column.Static( "Transfers", this.$t( "AGENTKB_conversationsTransfers" ) as string ),
            Column.Static( "Closed", this.$t( "AGENTKB_conversationsClosed" ) as string )
        ];

        const user = this.$route.query.user;

        if( typeof user === "string" )
            this.userEmail = user;

        this.fetch();
    }

    private beforeDestroy(): void {
        this.alive = false;
        window.clearTimeout( this.timer );
    }

    private selectPeriod( period: Period ): void {
        this.period = period;
        this.reload();
    }

    private selectKind( kind: Kind ): void {
        this.kind = kind;
        this.reload();
    }

    private onUserInput(): void {
        window.clearTimeout( this.timer );

        this.timer = window.setTimeout( () => this.reload(), 400 );
    }

    private reload(): void {
        this.pagerSorter.Page = 1;
        this.selected = null;
        this.fetch();
    }

    private onPager( pagerSorter: PagerSorter ): void {
        this.pagerSorter = pagerSorter;
        this.fetch();
    }

    private openRow( action: string, rows: ConversationRow[] ): void {
        if( action === "open" && rows.length > 0 )
            this.selected = rows[ 0 ];
    }

    private closeThread(): void {
        this.selected = null;
    }

    private filters(): AgentKbFilters {
        const from = new Date();
        const to = new Date();

        from.setDate( from.getDate() - this.period + 1 );
        to.setDate( to.getDate() + 1 );

        const filters: AgentKbFilters = {
            from: isoDay( from ),
            to: isoDay( to ),
            userEmail: this.userEmail.trim().toLowerCase(),
            page: this.pagerSorter.Page,
            limit: this.pagerSorter.Limit
        };

        if( this.kind === "down" )
            filters.score = "-1";

        if( this.kind === "sent" )
            filters.support = "sent";

        return filters;
    }

    private async fetch(): Promise<void> {
        this.loading = true;

        const [ page, error ] = await AgentKb.Conversations( this.filters() );

        if( !this.alive )
            return;

        this.loading = false;

        if( error !== undefined || page === undefined ) {
            if( error !== undefined )
                EventBus.Publish( "Error", error );

            return;
        }

        this.rows = page.items.map( item => this.toRow( item ) );
        this.pagerSorter.Total = page.total;

        void LoadCompanies( page.items.map( item => item.groupId ) );
    }

    private toRow( item: AgentKbConversation ): ConversationRow {
        return {
            ...item,
            Id: item.conversationId,
            Start: this.formatDate( item.debut ),
            User: item.userEmail,
            Question: item.premiereQuestion,
            /* Sous un filtre de vote ou de transfert, `tours` ne compte que les
               echanges retenus : on ne l'affiche pas, il mentirait. */
            Turns: this.kind === "all" ? String( item.tours ) : "—",
            Votes: `${ item.pouces.haut } / ${ item.pouces.bas }`,
            Transfers: String( item.transferts ),
            Closed: item.closedAt !== null ? this.formatDate( item.closedAt ) : ""
        };
    }

    private groupLabel( group: number ): string {
        const company = CompanyOf( group );

        return this.$t( "AGENTKB_columnGroup" ) + " " + String( group )
            + ( company !== "" ? " · " + company : "" );
    }

    private kindLabel( kind: Kind ): string {
        return this.$t( "AGENTKB_conversationsKind_" + kind ) as string;
    }

    private periodLabel( period: Period ): string {
        return this.$t( "AGENTKB_period" + period ) as string;
    }

    private isSelected( row: ConversationRow ): boolean {
        return this.selected !== null && this.selected.Id === row.Id;
    }

    private formatDate( iso: string ): string {
        const date = new Date( iso );

        if( isNaN( date.getTime() ) )
            return "—";

        return date.toLocaleDateString( "fr-FR", {
            day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
        } );
    }
}
